import { DecisionType } from "@/common/decisions";
import {
  ArticlesOfAssociationChangeMode,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  extractSectionWithMaxScoreFromArticles,
  SharesForShareholder,
  shareTypesWithShares,
  updateArticlesModeModel,
} from "@/dbot/common/commonDomain";
import { ShareholderDTO } from "@/types/Shareholder";
import { makeAutoObservable } from "mobx";
import { newUuid } from "@/utils/uuid";
import { ChangeArticlesOfAssociationModel } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "@/common/commonForms";
import { ShareType, ShareTypeCode } from "@/common/share";
import {
  NewShareClassController,
  ShareClassApi,
} from "@/shareholderRegister/transactions/shareClassDomain";
import { AxiosInstance } from "axios";
import { Language } from "@/i18n/languages";

export type ConversionOfShares = {
  type: DecisionType.conversionOfShares;
  fromShareTypeId?: number;
  toShareTypeId?: number;
  toNewShareTypeCode?: ShareTypeCode;
  convertAllShares: boolean;
  sharesPerShareholder: ConvertSharesPerShareholder[];
  updateArticlesAutomatically?: boolean;
  articleOfAssociationNumber?: number;
  articleChanges: ArticlesOfAssociationChangeMode[];
};

export type ConvertSharesPerShareholder = {
  shareholderId: number;
  shareholderName: string;
  numberOfShares: number;
};

export class ConversionOfSharesForm {
  readonly type = DecisionType.conversionOfShares;
  fromShareTypeModel: RadioFieldModel<ShareType>;
  toShareTypeModel: RadioFieldModel<ShareType | null>;
  articleOfAssociationNumber: ArticleSelectorModel;
  convertAllShares: BooleanFieldModel;
  sharesPerShareholder: ConvertSharesPerShareholderModel[];
  newShareClassController?: NewShareClassController;
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  articleChangeModel: ChangeArticlesOfAssociationModel;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<ConversionOfShares>,
    readonly data?: ConversionOfShares,
  ) {
    this.fromShareTypeModel = new RadioFieldModel({
      value:
        this.fromShareTypes.find((shareType) => shareType.sharetypeId === data?.fromShareTypeId) ??
        this.fromShareTypes[0],
      options: () => this.fromShareTypes,
      labelFn: () => ({ translated: "" }),
      idFn: (shareType) => String(shareType?.sharetypeId),
      onChange: () => (this.toShareTypeModel = this.createToShareModel()),
    });

    this.toShareTypeModel = this.createToShareModel(
      this.toShareTypes.find((st) => st.sharetypeId === data?.toShareTypeId),
    );

    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
    this.convertAllShares = new BooleanFieldModel({ value: data?.convertAllShares });

    this.sharesPerShareholder = this.context.initialState.shareholders.map(
      (s) =>
        new ConvertSharesPerShareholderModel(
          s,
          data?.sharesPerShareholder?.find((d) => d.shareholderId === s.id)?.numberOfShares,
          this.sharesForShareholders,
        ),
    );

    this.updateArticlesAutomatically = updateArticlesModeModel(data?.updateArticlesAutomatically);

    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get sharesForShareholders() {
    return this.context.inputState?.sharesForShareholders;
  }

  get fromShareTypes() {
    return shareTypesWithShares(
      this.context.initialState.shareTypes,
      this.context.inputState?.numberOfShares,
    );
  }

  get toShareTypes() {
    return this.context.initialState.shareTypes.filter((st) =>
      this.context.inputState?.numberOfShares.some((s) => s.shareTypeCode === st.shareTypeCode),
    );
  }

  private createToShareModel(value?: ShareType) {
    return new RadioFieldModel({
      value:
        this.toShareTypes.find((shareType) => shareType.sharetypeId === value?.sharetypeId) ?? null,
      options: () =>
        this.toShareTypes.filter(
          (shareType) => shareType.sharetypeId !== this.fromShareTypeModel.value?.sharetypeId,
        ),
      labelFn: () => ({ translated: "" }),
      idFn: (shareType) => String(shareType?.sharetypeId),
      required: true,
    });
  }

  validate() {
    this.fromShareTypeModel.validate();
    this.toShareTypeModel.validate();
    this.updateArticlesAutomatically.validate();
    this.articleChangeModel.validate();
    if (this.fromShareTypeModel.value === this.toShareTypeModel.value) {
      this.toShareTypeModel.error = { key: "decisions.conversionOfShares.errorSameShareClass" };
    }
    this.sharesPerShareholder.forEach((s) => s.validate(this.fromShareTypeModel.value));
    return this.isValid;
  }

  get isUpdateArticlesAutomatically() {
    return this.updateArticlesAutomatically.value === true;
  }

  get isUpdateArticlesManually() {
    return this.updateArticlesAutomatically.value === false;
  }

  get isArticleChangesValid(): boolean {
    const isAutomaticArticleChangesValid =
      this.isUpdateArticlesAutomatically && this.articleOfAssociationNumber.isValid;
    const isManualArticleChangesValid =
      this.isUpdateArticlesManually && this.articleChangeModel.isValid;
    return isAutomaticArticleChangesValid || isManualArticleChangesValid;
  }

  get isValid(): boolean {
    const fromValue = this.fromShareTypeModel.value;
    const toValue = this.toShareTypeModel.value;
    return (
      this.fromShareTypeModel.isValid &&
      this.toShareTypeModel.isValid &&
      fromValue?.sharetypeId !== toValue?.sharetypeId &&
      this.sharesPerShareholder.every((s) => s.isValid) &&
      this.updateArticlesAutomatically.isValid &&
      this.isArticleChangesValid
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  init() {
    if (this.data === undefined || this.data.articleOfAssociationNumber === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success": {
        if (this.articleOfAssociationNumber.selected === undefined) {
          this.articleOfAssociationNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
      }
    }
  }

  openNewShareClassDialog(http: AxiosInstance, lang: Language) {
    const shareTypeCodes = Object.values(ShareTypeCode).filter(
      (s) => !this.toShareTypes.some((st) => st.shareTypeCode === s) && s !== ShareTypeCode.UNKNOWN,
    );

    this.newShareClassController = new NewShareClassController(
      this.context.orgNo,
      new ShareClassApi(http),
      this.closeNewShareClassDialog,
      shareTypeCodes,
      lang,
      this.addNewShareType,
    );
  }

  closeNewShareClassDialog() {
    this.newShareClassController = undefined;
  }

  addNewShareType(shareType: ShareType) {
    this.toShareTypeModel.setValue(shareType);
  }
}

export class ConvertSharesPerShareholderModel {
  id = newUuid();
  shareholder: ShareholderDTO;
  numberOfShares: NumberFieldModel;

  constructor(
    shareholder: ShareholderDTO,
    numberOfShares?: number,
    readonly shareForShareholder?: SharesForShareholder[],
  ) {
    this.shareholder = shareholder;
    this.numberOfShares = new NumberFieldModel({
      isInteger: true,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
  }

  currentNumberOfSharesForShareType(shareType: ShareType): number | undefined {
    return this.shareForShareholder?.find(
      (s) => s.shareTypeId === shareType.sharetypeId && s.shareholderId === this.shareholder.id,
    )?.numberOfShares;
  }

  validate(shareType: ShareType) {
    this.numberOfShares.validate();

    const parsedValue = this.numberOfShares.parsedValue;
    const shareholderShares = this.currentNumberOfSharesForShareType(shareType);
    if (parsedValue) {
      if (shareholderShares) {
        if (parsedValue > shareholderShares) {
          this.numberOfShares.error = {
            key: "transactions.conversionOfShares.numberExceededError",
          };
        }
      } else {
        this.numberOfShares.error = {
          key: "transactions.conversionOfShares.numberExceededError",
        };
      }
    }
  }

  get isValid() {
    return this.numberOfShares.isValid;
  }
}
